import { CheckCircle } from "lucide-react";
import { useScrollReveal, useAnimatedCounter } from "../hooks/useAnimations";
import { assets } from "../lib/assets";

function Counter({ value, visible }) {
  const count = useAnimatedCounter(value, 1800, visible);
  const isNum = !isNaN(parseInt(value));
  const suffix = value.replace(/[0-9]/g, '');
  return <>{isNum ? count : value}{isNum ? suffix : ''}</>;
}

export default function About({ t }) {
  const [ref, vis] = useScrollReveal();
  const [bentoRef, bentoVis] = useScrollReveal({ threshold: 0.1 });

  return (
    <section id="about" data-testid="about-section" className="py-24 lg:py-32 bg-white relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div ref={ref} className={`reveal ${vis ? 'visible' : ''}`}>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-[2px] bg-accent-green" />
            <span className="text-accent-green text-xs font-semibold tracking-[0.25em] uppercase">
              {t.about.sectionLabel}
            </span>
          </div>

          <div className="grid lg:grid-cols-2 gap-4 lg:gap-12 mb-16">
            <h2
              data-testid="about-title"
              className="font-heading font-bold text-4xl sm:text-5xl lg:text-[3.2rem] text-brand-blue leading-[1.08] tracking-tight"
            >
              {t.about.title}
            </h2>
            <div className="flex items-end">
              <p className="text-slate-600 text-base lg:text-lg leading-[1.8] max-w-lg">
                {t.about.description}
              </p>
            </div>
          </div>
        </div>

        <div
          ref={bentoRef}
          className={`grid grid-cols-1 gap-3 lg:gap-4 stagger-children ${bentoVis ? 'visible' : ''}`}
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 lg:gap-4">
            <div className="rounded-2xl overflow-hidden relative group min-h-[220px] lg:min-h-[280px]">
              <img
                src={assets.aboutLab}
                alt="Shandong Prit laboratory and research facility for veterinary feed additives"
                className="w-full h-full min-h-[220px] lg:min-h-[280px] object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-emerald-950/70 via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 p-5">
                <div className="text-emerald-50/60 text-xs tracking-[0.2em] uppercase mb-1">Since</div>
                <div className="text-emerald-50 font-heading font-bold text-5xl">2018</div>
              </div>
            </div>
            <div className="rounded-2xl bg-card-mint border border-[#2ECC71]/20 p-6 lg:p-8 flex flex-col justify-center min-h-[220px] lg:min-h-[280px]">
              <p className="text-[#333333] text-base lg:text-lg leading-relaxed">
                {t.about.description2}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 lg:gap-4">
            {t.about.stats.slice(1).map((stat, i) => (
              <div
                key={i}
                data-testid={`stat-${i}`}
                className="rounded-2xl border border-[#2ECC71]/20 bg-card-mint p-5 lg:p-6 flex flex-col justify-between min-h-[140px] hover-lift hover-glow cursor-default group"
              >
                <div className="text-accent-green font-heading font-bold text-3xl lg:text-4xl tracking-tight group-hover:opacity-90 transition-colors">
                  <Counter value={stat.value} visible={bentoVis} />
                </div>
                <div className="text-[#333333] text-sm font-medium mt-auto">{stat.label}</div>
              </div>
            ))}
            <div className="rounded-2xl bg-emerald-900 p-5 lg:p-6 text-white relative overflow-hidden noise-overlay hover-lift flex flex-col justify-between min-h-[140px]">
              <div className="relative z-10 flex flex-col h-full">
                <h3 className="font-heading font-semibold text-sm lg:text-base mb-3 text-emerald-200 uppercase tracking-[0.15em]">
                  {t.about.philosophy}
                </h3>
                <div className="space-y-2 flex-1">
                  {t.about.philosophyItems.map((item, i) => (
                    <div key={i} className="flex items-center gap-2.5">
                      <CheckCircle className="w-4 h-4 lg:w-5 lg:h-5 text-emerald-400 flex-shrink-0" />
                      <span className="text-emerald-100 text-sm lg:text-base">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
